import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Upload, Download, Replace, Trash2, AlertTriangle, ChevronDown, ChevronRight, X } from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog } from '../../lib/confirmStore';
import type { OrgDocument } from '../../types';

/**
 * OrgDocumentsSection — supporting documents for a Referrer, Sponsor,
 * or Employer record (admin detail views). Backed by
 * /api/admin/orgs/:orgType/:orgId/documents (server/orgDocumentsCore).
 * The lifecycle status is computed by the server from the expiry date;
 * this component never invents or edits a status.
 */

const DOC_TYPE_PRESETS = [
  "Workers' Compensation Certificate",
  'Business Registration Certificate',
  'Business Licence',
  'Insurance Certificate',
  'Compliance Document',
  'Other',
];

const STATUS_STYLES: Record<string, string> = {
  Valid: 'bg-emerald-100 text-emerald-700',
  Expired: 'bg-red-100 text-red-700',
  'Expiring Soon': 'bg-amber-100 text-amber-700',
  'No Expiry Date': 'bg-slate-100 text-slate-500',
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function OrgDocumentsSection({
  orgType,
  orgId,
  canEdit = true,
}: {
  orgType: 'referrer' | 'employer' | 'sponsor';
  orgId: string;
  canEdit?: boolean;
}) {
  const [documents, setDocuments] = useState<OrgDocument[]>([]);
  const [expiringSoonCount, setExpiringSoonCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState<string | null>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const replaceTargetRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const data = await safeFetch(`/api/admin/orgs/${orgType}/${orgId}/documents`);
      setDocuments(data.documents || []);
      setExpiringSoonCount(data.expiringSoonCount || 0);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [orgType, orgId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!name.trim() || !type.trim() || !file) {
      notifyToast('error', 'Document name, type, and a file are required');
      return;
    }
    setSaving(true);
    try {
      const fileBase64 = await fileToBase64(file);
      await safeFetch(`/api/admin/orgs/${orgType}/${orgId}/documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          type: type.trim(),
          fileName: file.name,
          fileBase64,
          expiryDate: expiryDate || null,
        }),
      });
      notifyToast('success', 'Document uploaded');
      setName(''); setType(''); setExpiryDate(''); setFile(null); setShowForm(false);
      await load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Upload failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (doc: OrgDocument) => {
    try {
      const data = await safeFetch(`/api/admin/orgs/${orgType}/${orgId}/documents/${doc.docId}/download`);
      if (data.url) window.open(data.url, '_blank', 'noopener');
    } catch (err: any) {
      notifyToast('error', err.message || 'Download failed');
    }
  };

  const handleReplacePick = (docId: string) => {
    replaceTargetRef.current = docId;
    replaceInputRef.current?.click();
  };

  const handleReplaceFile = async (picked: File | null) => {
    const docId = replaceTargetRef.current;
    replaceTargetRef.current = null;
    if (!picked || !docId) return;
    try {
      const fileBase64 = await fileToBase64(picked);
      await safeFetch(`/api/admin/orgs/${orgType}/${orgId}/documents/${docId}/replace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: picked.name, fileBase64 }),
      });
      notifyToast('success', 'Document replaced');
      await load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Replace failed');
    }
  };

  const handleRemove = async (doc: OrgDocument) => {
    if (!(await confirmDialog(`Remove "${doc.name}"? The record and its history are kept, but the file will no longer be available.`))) return;
    try {
      await safeFetch(`/api/admin/orgs/${orgType}/${orgId}/documents/${doc.docId}`, { method: 'DELETE' });
      notifyToast('success', 'Document removed');
      await load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Remove failed');
    }
  };

  return (
    <div className="border border-slate-200 rounded-2xl p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
          <FileText className="h-3.5 w-3.5" /> Documents
        </h3>
        <div className="flex items-center gap-2">
          {expiringSoonCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-amber-100 text-amber-700">
              <AlertTriangle className="h-3 w-3" /> {expiringSoonCount} expiring soon
            </span>
          )}
          {canEdit && (
            <button onClick={() => setShowForm((v) => !v)} className="flex items-center gap-1 px-2.5 py-1.5 btn-primary text-white rounded-lg text-[11px] font-bold cursor-pointer">
              {showForm ? <X className="h-3 w-3" /> : <Upload className="h-3 w-3" />} {showForm ? 'Close' : 'Upload Document'}
            </button>
          )}
        </div>
      </div>

      {showForm && canEdit && (
        <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-xl p-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Document name" className="px-2.5 py-2 text-xs border border-slate-200 rounded-lg bg-white" />
          <input value={type} onChange={(e) => setType(e.target.value)} placeholder="Document type" list="org-doc-type-presets" className="px-2.5 py-2 text-xs border border-slate-200 rounded-lg bg-white" />
          <datalist id="org-doc-type-presets">
            {DOC_TYPE_PRESETS.map((p) => <option key={p} value={p} />)}
          </datalist>
          <label className="flex items-center gap-2 text-[11px] text-slate-500">
            Expiry date (optional)
            <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white" />
          </label>
          <label className="flex items-center gap-2 text-[11px] text-slate-500">
            File
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" />
          </label>
          <div className="sm:col-span-2 flex justify-end">
            <button onClick={handleUpload} disabled={saving} className="px-3 py-1.5 btn-primary text-white rounded-lg text-[11px] font-bold cursor-pointer disabled:opacity-50">
              {saving ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </div>
      )}

      <input
        ref={replaceInputRef}
        type="file"
        className="hidden"
        onChange={(e) => { handleReplaceFile(e.target.files?.[0] || null); e.target.value = ''; }}
      />

      {loading ? (
        <p className="text-xs text-slate-500 py-2">Loading documents…</p>
      ) : documents.length === 0 ? (
        <p className="text-xs text-slate-500 py-2">No documents uploaded yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {documents.map((doc) => (
            <li key={doc.docId} className="py-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-700 truncate">{doc.name}</p>
                  <p className="text-[11px] text-slate-500 truncate">
                    {doc.type} · {doc.fileName} · uploaded {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : ''}
                    {doc.expiryDate ? ` · expires ${doc.expiryDate}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${STATUS_STYLES[doc.status || ''] || 'bg-slate-100 text-slate-500'}`}>
                    {doc.status}
                  </span>
                  <button onClick={() => handleDownload(doc)} className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-600 cursor-pointer" title="View / Download"><Download className="h-3.5 w-3.5" /></button>
                  {canEdit && (
                    <>
                      <button onClick={() => handleReplacePick(doc.docId)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer" title="Replace"><Replace className="h-3.5 w-3.5" /></button>
                      <button onClick={() => handleRemove(doc)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 cursor-pointer" title="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
                    </>
                  )}
                  <button onClick={() => setExpandedHistory(expandedHistory === doc.docId ? null : doc.docId)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer" title="History">
                    {expandedHistory === doc.docId ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
              {expandedHistory === doc.docId && (
                <ul className="mt-1.5 ml-1 space-y-0.5">
                  {(doc.history || []).map((h, i) => (
                    <li key={i} className="text-[11px] text-slate-500">
                      {h.at ? new Date(h.at).toLocaleString() : ''} — {h.action} by {h.byName || h.by}{h.note ? ` — ${h.note}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
